import { CircleInfoIcon } from "@xola/icons";
import { Label, SubmitButton, Tooltip } from "@xola/ui-kit";
import { useBoolean } from "ahooks";
import { FormField, InputField, Loader, SwitchField } from "common";
import { Field, FieldProps } from "formik";
import React from "react";
import { useHistory } from "react-router-dom";
import {
    AmountType,
    FeeScope,
    FeeScopeModifier,
    LineItemTemplate,
    LineItemType,
    Permission,
    ProductObject,
    PurchaseRule,
    PurchaseRuleLink,
    SourceCopy,
    TaxesAndFeesSource,
} from "types";
import {
    getIsExperienceSelectionDisabled,
    getIsMerchandiseSelectionDisabled,
    isMerchandiseOnlyTerms,
    saveLineItemTemplate,
    savePurchaseRule,
    savePurchaseRuleLink,
} from "./TaxAndFeeFormHelper";
import { TaxAndFeeFormValues, useTaxAndFeeFormValues, validationSchema } from "./TaxAndFeeFormValues";
import { TaxAndFeeTermsFields } from "./TaxAndFeeTermsFields";
import { Form } from "@/components/Common/Form";
import { IsGranted } from "@/components/Common/IsGranted";
import { ProductSelectorModal } from "@/components/Common/ProductSelectorModal";
import { RadioInput } from "@/components/Common/RadioInput";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { SelectedLineItemTemplateListInput } from "@/components/Common/SelectedLineItemTemplateListInput";
import { SelectedPartnerListInput } from "@/components/Common/SelectedPartnerListInput";
import { SelectedProductListInput } from "@/components/Common/SelectedProductListInput";
import { XolaFormik } from "@/components/Common/XolaFormik";
import { ContentLayout } from "@/components/Layout/ContentLayout";
import { ScrollToFieldError } from "@/helpers/validationHelper";
import { useEquipmentResourcesWithTemplates } from "@/hooks/resources/useEquipmentResourcesWithTemplates";
import { useSeller } from "@/hooks/useAccount";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { useCollection } from "@/hooks/useCollection";
import { useInvalidate } from "@/hooks/useInvalidation";
import { useResource } from "@/hooks/useResource";
import {
    getSelectedExperiences,
    getSelectedMemberships,
    getSelectedMerchandise,
    getSelectionState,
} from "@/models/SelectionState";

const purchaseSourceValues = Object.values(TaxesAndFeesSource);

interface EditTaxAndFeeProps {
    purchaseRule: PurchaseRule | null;
    purchaseRuleLinkId?: ID;
    isInDrawer?: boolean;
    onClose?: () => void;
    defaultMerchandiseId?: ID;
}

export const EditTaxAndFee = ({
    purchaseRule,
    purchaseRuleLinkId,
    isInDrawer,
    onClose,
    defaultMerchandiseId,
}: EditTaxAndFeeProps) => {
    const [isProductSelectorModalOpen, { setTrue: showProductSelector, setFalse: hideProductSelector }] =
        useBoolean(false);
    const [isMembershipSelectorModalOpen, { setTrue: showMembershipSelector, setFalse: hideMembershipSelector }] =
        useBoolean(false);
    const [isMerchandiseSelectorModalOpen, { setTrue: showMerchandiseSelector, setFalse: hideMerchandiseSelector }] =
        useBoolean(false);
    const seller = useSeller();
    const history = useHistory();
    const invalidate = useInvalidate();
    const equipmentResources = useEquipmentResourcesWithTemplates();
    const validEquipmentTemplateIds = equipmentResources.map((r) => r.templateId);

    const handleCloseOrRedirect = () => {
        if (onClose) {
            onClose();
            return;
        }

        history.push("/settings/taxesAndFees");
    };

    const { data: purchaseRuleLink } = useResource<PurchaseRuleLink>(["purchaseRuleLinks", purchaseRuleLinkId]);
    const { data: subTotalLineItemTemplates, isLoading: isLoadingSubTotalLineItemTemplates } =
        useCollection<LineItemTemplate>(["lineItemTemplates"], {
            "code[in]":
                "demographic_subtotal,equipment_subtotal,merchandise_subtotal,pickup_subtotal,discount_subtotal",
        });
    const { data: unitLineItemTemplates, isLoading: isLoadingUnitTemplates } = useAutoFetchCollection<LineItemTemplate>(
        ["lineItemTemplates"],
        {
            "id[in]": getSelectedTemplateIdsFromPurchaseRule(purchaseRule).join(","),
        },
        {
            enabled: !!purchaseRule?.id,
        },
    );

    const values = useTaxAndFeeFormValues(
        purchaseRule,
        purchaseRuleLink,
        subTotalLineItemTemplates,
        unitLineItemTemplates,
        defaultMerchandiseId,
    );

    const handleSubmit = async (values: TaxAndFeeFormValues) => {
        const lineItemTemplateId = purchaseRule?.actions[0]?.template?.id;
        const { data: template } = await saveLineItemTemplate(seller.id, values, lineItemTemplateId);
        const { data: savedPurchaseRule } = await savePurchaseRule(
            values,
            template,
            purchaseRule,
            subTotalLineItemTemplates,
        );
        await savePurchaseRuleLink(seller.id, savedPurchaseRule.id!, values, purchaseRuleLink?.id);

        invalidate("purchaseRules", purchaseRule?.id);
        invalidate("purchaseRuleLinks", purchaseRuleLink?.id);
        invalidate("lineItemTemplates", template.id);

        handleCloseOrRedirect();
    };

    const handleCancel = () => {
        handleCloseOrRedirect();
    };

    if (isLoadingSubTotalLineItemTemplates || isLoadingUnitTemplates) {
        return <Loader />;
    }

    return (
        <ContentLayout>
            <XolaFormik
                enableReinitialize
                validationSchema={validationSchema}
                initialValues={values}
                onSubmit={handleSubmit}
            >
                {({ handleSubmit, values, setFieldValue, errors, isSubmitting }) => {
                    const isExperienceSelectionDisabled = getIsExperienceSelectionDisabled(values);
                    if (isExperienceSelectionDisabled) {
                        values.selectedExperiences = { all: false, experiences: [] };
                    }

                    const isMembershipSelectionDisabled = getIsMembershipSelectionDisabled(values);
                    if (isMembershipSelectionDisabled) {
                        values.selectedMemberships = { all: false, memberships: [] };
                    }

                    const isMerchandiseSelectionDisabled = getIsMerchandiseSelectionDisabled(values);
                    if (isMerchandiseSelectionDisabled) {
                        values.selectedMerchandise = { all: false, merchandise: [] };
                    }

                    return (
                        <Form>
                            <fieldset className="mb-16 flex-1 space-y-4" disabled={isSubmitting}>
                                <div className="mb-8 space-y-4">
                                    <h2 className="mt-4">Terms</h2>
                                    <FormField name="title" label="Title">
                                        <InputField isRequired />
                                    </FormField>

                                    <div className="space-y-0.5 text-gray-darker">
                                        <Label className="whitespace-nowrap">
                                            Type
                                            <Tooltip
                                                content="Fees are subject to tax assessment. If you have a tax and a fee configured on a product, then taxes will be assessed on the purchase price including fees."
                                                className="ml-1"
                                            >
                                                <CircleInfoIcon />
                                            </Tooltip>
                                        </Label>

                                        <div className="flex space-x-6">
                                            <RadioInput
                                                checked={values.type === LineItemType.FEE}
                                                disabled={!!purchaseRule}
                                                onChange={() => {
                                                    setFieldValue("type", LineItemType.FEE);
                                                }}
                                            >
                                                Fee
                                            </RadioInput>

                                            <RadioInput
                                                checked={values.type === LineItemType.TAX}
                                                disabled={!!purchaseRule}
                                                onChange={() => {
                                                    setFieldValue("type", LineItemType.TAX);
                                                }}
                                            >
                                                Tax
                                            </RadioInput>
                                        </div>
                                    </div>

                                    <TaxAndFeeTermsFields />
                                </div>

                                <hr className="text-gray-light" />

                                <div className="space-y-4">
                                    <h4 className="font-bold">Applies to</h4>

                                    {showDemographics(values) ? (
                                        <SelectedLineItemTemplateListInput
                                            isSelectAll
                                            label="Demographics"
                                            error={errors.selectedDemographics?.selected?.toString()}
                                            type={LineItemType.DEMOGRAPHIC}
                                            value={values.selectedDemographics}
                                            onChange={(demographics) => {
                                                setFieldValue("selectedDemographics", demographics);
                                            }}
                                        />
                                    ) : null}

                                    {showEquipment(values) ? (
                                        <SelectedLineItemTemplateListInput
                                            isSelectAll
                                            label="Equipment"
                                            error={errors.selectedEquipment?.selected?.toString()}
                                            type={LineItemType.EQUIPMENT}
                                            allowedIds={validEquipmentTemplateIds}
                                            value={values.selectedEquipment}
                                            onChange={(equipment) => {
                                                setFieldValue("selectedEquipment", equipment);
                                            }}
                                        />
                                    ) : null}

                                    <SelectedProductListInput
                                        isSelectAll
                                        label="Experiences"
                                        isDisabled={isExperienceSelectionDisabled}
                                        selectedProducts={values.selectedExperiences}
                                        error={errors.selectedExperiences?.experiences}
                                        onEditClick={() => {
                                            showProductSelector();
                                        }}
                                        onChange={(selectionState) => {
                                            setFieldValue(
                                                "selectedExperiences",
                                                getSelectedExperiences(selectionState),
                                            );
                                        }}
                                    />

                                    <Field name="selectedExperiences">
                                        {({ field, form }: FieldProps) => (
                                            <ProductSelectorModal
                                                value={getSelectionState(field.value)}
                                                isOpen={isProductSelectorModalOpen}
                                                objects={[ProductObject.EXPERIENCE]}
                                                title="Product Restrictions"
                                                description="Restrict the products to which this tax/fee is applicable"
                                                onClose={() => {
                                                    hideProductSelector();
                                                }}
                                                onConfirm={(selectionState) => {
                                                    form.setFieldValue(
                                                        "selectedExperiences",
                                                        getSelectedExperiences(selectionState),
                                                    );
                                                }}
                                            />
                                        )}
                                    </Field>

                                    <IsGranted permissions={Permission.MEMBERSHIPS_MODIFY}>
                                        <SelectedProductListInput
                                            isSelectAll
                                            label="Memberships"
                                            isDisabled={isMembershipSelectionDisabled}
                                            selectedProducts={values.selectedMemberships}
                                            error={errors.selectedExperiences?.experiences}
                                            onEditClick={() => {
                                                showMembershipSelector();
                                            }}
                                            onChange={(selectionState) => {
                                                setFieldValue(
                                                    "selectedMemberships",
                                                    getSelectedMemberships(selectionState),
                                                );
                                            }}
                                        />

                                        <Field name="selectedMemberships">
                                            {({ field, form }: FieldProps) => (
                                                <ProductSelectorModal
                                                    value={getSelectionState(field.value)}
                                                    isOpen={isMembershipSelectorModalOpen}
                                                    objects={[ProductObject.MEMBERSHIP]}
                                                    title="Memberships Restrictions"
                                                    description="Restrict the memberships to which this tax/fee is applicable"
                                                    onClose={() => {
                                                        hideMembershipSelector();
                                                    }}
                                                    onConfirm={(selectionState) => {
                                                        form.setFieldValue(
                                                            "selectedMemberships",
                                                            getSelectedMemberships(selectionState),
                                                        );
                                                    }}
                                                />
                                            )}
                                        </Field>
                                    </IsGranted>

                                    <IsGranted permissions={Permission.MERCHANDISE_MODIFY}>
                                        <SelectedProductListInput
                                            isSelectAll
                                            label="Merchandise"
                                            isDisabled={isMerchandiseSelectionDisabled}
                                            selectedProducts={values.selectedMerchandise}
                                            error={errors.selectedExperiences?.experiences}
                                            onEditClick={() => {
                                                showMerchandiseSelector();
                                            }}
                                            onChange={(selectionState) => {
                                                setFieldValue(
                                                    "selectedMerchandise",
                                                    getSelectedMerchandise(selectionState),
                                                );
                                            }}
                                        />

                                        <Field name="selectedMerchandise">
                                            {({ field, form }: FieldProps) => (
                                                <ProductSelectorModal
                                                    value={getSelectionState(field.value)}
                                                    isOpen={isMerchandiseSelectorModalOpen}
                                                    objects={[ProductObject.WARE]}
                                                    title="Merchandise Restrictions"
                                                    description="Restrict the merchandise to which this tax/fee is applicable"
                                                    onClose={() => {
                                                        hideMerchandiseSelector();
                                                    }}
                                                    onConfirm={(selectionState) => {
                                                        form.setFieldValue(
                                                            "selectedMerchandise",
                                                            getSelectedMerchandise(selectionState),
                                                        );
                                                    }}
                                                />
                                            )}
                                        </Field>
                                    </IsGranted>

                                    {showAddOns(values) ? (
                                        <SelectedLineItemTemplateListInput
                                            isSelectAll
                                            label="Add-Ons"
                                            error={errors.selectedAddOns?.selected?.toString()}
                                            type={LineItemType.ADDON}
                                            value={values.selectedAddOns}
                                            onChange={(addons) => {
                                                setFieldValue("selectedAddOns", addons);
                                            }}
                                        />
                                    ) : null}

                                    <IsGranted permissions={Permission.MODIFY_PARTNERS_TAX_AND_FEE_SETTING}>
                                        <SelectedPartnerListInput
                                            isSelectAll
                                            label="Commercial channels"
                                            error={errors.selectedPartners?.selected?.toString()}
                                            value={{
                                                agents: values.selectedPartners,
                                                operators: values.selectedOperators,
                                            }}
                                            onChange={(partners) => {
                                                setFieldValue("selectedPartners", partners.agents);
                                                setFieldValue("selectedOperators", partners.operators);
                                            }}
                                        />
                                    </IsGranted>

                                    <FormField className="space-y-2" name="purchaseSources" label="Purchase Sources">
                                        {purchaseSourceValues.map((value) => (
                                            <div key={value}>
                                                <SwitchField
                                                    isChecked={values.purchaseSources.includes(value)}
                                                    onChange={(isChecked) => {
                                                        setFieldValue(
                                                            "purchaseSources",
                                                            getSelectedPurchaseSource(
                                                                isChecked,
                                                                values.purchaseSources,
                                                                value,
                                                            ),
                                                        );
                                                    }}
                                                >
                                                    {SourceCopy[value]}
                                                </SwitchField>
                                            </div>
                                        ))}
                                        <SwitchField
                                            isChecked={values.isAppAndIntegrationEnabled}
                                            onChange={(isChecked) => {
                                                setFieldValue("isAppAndIntegrationEnabled", isChecked);
                                            }}
                                        >
                                            Apps & Integrations
                                        </SwitchField>
                                    </FormField>
                                </div>
                            </fieldset>

                            {!isInDrawer ? (
                                <ContentLayout.Footer preferenceSidebar>
                                    <TaxAndFeeActionButtons
                                        isSubmitting={isSubmitting}
                                        handleCancel={handleCancel}
                                        onSubmit={handleSubmit}
                                    />
                                </ContentLayout.Footer>
                            ) : (
                                <div className="mt-6 flex space-x-2">
                                    <TaxAndFeeActionButtons
                                        isSubmitting={isSubmitting}
                                        handleCancel={handleCancel}
                                        onSubmit={handleSubmit}
                                    />
                                </div>
                            )}

                            <ScrollToFieldError />
                        </Form>
                    );
                }}
            </XolaFormik>
        </ContentLayout>
    );
};

interface TaxAndFeeActionButtonsProps {
    isSubmitting: boolean;
    onSubmit: () => void;
    handleCancel: () => void;
}

const TaxAndFeeActionButtons = ({ isSubmitting, onSubmit, handleCancel }: TaxAndFeeActionButtonsProps) => {
    return (
        <>
            <SubmitButton type="submit" isLoading={isSubmitting} onClick={onSubmit}>
                Save
            </SubmitButton>

            <SecondaryButton onClick={handleCancel}>Cancel</SecondaryButton>
        </>
    );
};

const showDemographics = (values: TaxAndFeeFormValues): boolean => {
    if (values.amountType === AmountType.ABSOLUTE && values.scope === FeeScope.PERSON) {
        return true;
    }

    return (
        values.amountType === AmountType.PERCENT &&
        (values.scopeModifierPerOuting?.includes(FeeScopeModifier.EXCLUDE_ADDON) ||
            values.scopeModifierPerOuting?.includes(FeeScopeModifier.ALL))
    );
};

const showEquipment = (values: TaxAndFeeFormValues): boolean => {
    if (values.amountType === AmountType.ABSOLUTE && values.scope === FeeScope.EQUIPMENT) {
        return true;
    }

    return (
        values.amountType === AmountType.PERCENT &&
        (values.scopeModifierPerOuting?.includes(FeeScopeModifier.EQUIPMENT_ONLY) ||
            values.scopeModifierPerOuting?.includes(FeeScopeModifier.ALL))
    );
};

const showAddOns = (values: TaxAndFeeFormValues): boolean => {
    if (values.amountType === AmountType.ABSOLUTE && values.scope === FeeScope.ADDON) {
        return true;
    }

    return (
        values.amountType === AmountType.PERCENT &&
        values.scopeModifierPerOuting?.includes(FeeScopeModifier.ADDON_ONLY) &&
        !values.scopeModifierPerOuting?.includes(FeeScopeModifier.ALL)
    );
};

function getSelectedPurchaseSource(isChecked: boolean, purchaseSources: string[], value: TaxesAndFeesSource): string[] {
    return isChecked ? [...purchaseSources, value] : purchaseSources.filter((item) => item !== value);
}

const getIsMembershipSelectionDisabled = (values: TaxAndFeeFormValues): boolean => {
    if (isMerchandiseOnlyTerms(values)) {
        return true;
    }

    if (values.amountType === AmountType.ABSOLUTE && values.scope === FeeScope.ADDON) {
        return true;
    }

    if (
        values.amountType === AmountType.PERCENT &&
        (values.scopeModifierPerOuting?.includes(FeeScopeModifier.ADDON_ONLY) ||
            values.scopeModifierPerOuting?.includes(FeeScopeModifier.EXCLUDE_ADDON))
    ) {
        return true;
    }

    if (
        values.amountType === AmountType.ABSOLUTE &&
        values.scope === FeeScope.PERSON &&
        values.scopeModifierPerPerson === FeeScopeModifier.PER_DAY
    ) {
        return true;
    }

    return false;
};

const getSelectedTemplateIdsFromPurchaseRule = (purchaseRule: PurchaseRule | null): ID[] => {
    if (!purchaseRule) {
        return [];
    }

    // Tax & fee will have only 1 action and the selected templates will be stored in templateIds key
    return purchaseRule.actions?.[0]?.shortcut?.options?.templateIds ?? [];
};
